import type { RtCoverageInfo } from '@mbus/shared';

/**
 * リアルタイムデータの提供状況バナー。
 * 「このエリアはリアルタイム対応」/「時刻表のみ」を正直に表示して
 * 利用者がデータの鮮度を誤解しないようにする。
 *
 * coverage.feeds は「表示エリアの停留所を供給しているフィード」のみ。
 * 事業者名は長くなり得るため、モバイルでも溢れないよう件数に丸める。
 */
export function RtBanner({ coverage }: { coverage: RtCoverageInfo }) {
  const feeds = coverage.feeds ?? [];
  if (feeds.length === 0) return null;

  const live = feeds.filter((f) => f.state === 'live');
  const stale = feeds.filter((f) => f.state === 'stale');
  const hasRt = live.length + stale.length > 0;

  // 事業者名の要約（最大 2 件 + 「他N」）。長い連結を避ける。
  const summarize = (names: string[]): string => {
    const uniq = [...new Set(names)];
    if (uniq.length <= 2) return uniq.join('・');
    return `${uniq.slice(0, 2).join('・')} 他${uniq.length - 2}`;
  };

  if (live.length > 0 && stale.length === 0) {
    return (
      <div className="banner banner-rt">
        <span className="banner-dot">●</span>
        <span className="banner-text">
          リアルタイム対応
          <span className="banner-sub">（{summarize(live.map((f) => f.name))}）</span>
        </span>
      </div>
    );
  }
  if (live.length > 0) {
    return (
      <div className="banner banner-mixed">
        <span className="banner-dot">●</span>
        <span className="banner-text">一部リアルタイム対応 / 一部は時刻表のみ</span>
      </div>
    );
  }
  if (stale.length > 0) {
    return (
      <div className="banner banner-stale">
        <span className="banner-dot">⚠</span>
        <span className="banner-text">リアルタイム情報を取得できません — 時刻表で表示中</span>
      </div>
    );
  }
  // RT 提供フィードが無いエリア（時刻表のみ）
  if (!hasRt) {
    return (
      <div className="banner banner-sched">
        <span className="banner-dot">○</span>
        <span className="banner-text">この地域は時刻表ベースの表示です</span>
      </div>
    );
  }
  return null;
}
